import { helpers, mui, React, ts, ui, useNavigate, useSelector } from '_core';

import * as utils from './time-series-utils';

type TimeSeriesFormProps = {
  open: boolean;
  setOpen: (_open: boolean) => void;
};

const TimeSeriesForm = ({ open, setOpen }: TimeSeriesFormProps) => {
  const timeSeries = useSelector((state) => state.resources.time_series as ts.types.timeSeries.TimeSeries[]);
  const workspaces = useSelector((state) => state.resources.workspaces as ts.types.workspace.Workspace[]);
  const currentWorkspaceId = useSelector((state) => state.ui.currentWorkspaceId);

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = React.useState('');

  const [name, setName] = React.useState('');
  const [handle, setHandle] = React.useState('');
  const [template, setTemplate] = React.useState<ts.types.timeSeries.TimeSeries>();
  const [loadingCreate, setLoadingCreate] = React.useState(false);
  const [workspace, setWorkspace] = React.useState(
    () => workspaces.find((w) => w.id == currentWorkspaceId) || workspaces[0]
  );

  // Create handle while typing name
  React.useEffect(() => {
    setHandle(helpers.createHandle(name, 't'));
  }, [name]);

  const handleSubmit = async () => {
    setErrorMessage('');

    // Validation
    if (!name || !handle) {
      setErrorMessage('All attributes are required.');
      setLoadingCreate(false);
      return;
    }

    setLoadingCreate(true);

    const newTimeSeries = utils.definition.getNewTimeSeries(name, handle, template);
    utils.apiProcedures.createTimeSeries(
      newTimeSeries,
      async (data) => {
        if (workspace)
          await helpers.resourcesUtils.addToWorkspace(data.id, ts.enums.UI_RESOURCE_TYPE_ENUM.TIME_SERIES, workspace);
        setOpen(false);
        navigate(`/time_series/${data.id}`);
      },
      async (err) => {
        setErrorMessage(err);
        setLoadingCreate(false);
      }
    );
  };

  const formContent = () => (
    <mui.core.Box mt={3}>
      <mui.core.Box mb={4}>
        <mui.core.Grid container spacing={3}>
          <mui.core.Grid size={{ xs: 12, sm: 6 }}>
            <mui.core.TextField
              autoFocus
              id="name"
              label={'Time series name'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              variant="outlined"
              required
              size="small"
              inputProps={{
                autocomplete: 'off',
              }}
            />
          </mui.core.Grid>
          <mui.core.Grid size={{ xs: 12, sm: 6 }}>
            <mui.core.TextField
              id="handle"
              label={'Time series handle'}
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              fullWidth
              variant="outlined"
              required
              size="small"
            />
          </mui.core.Grid>
        </mui.core.Grid>
      </mui.core.Box>
      <mui.core.Box py={4}>
        <ui.ResourceAutocomplete<ts.types.timeSeries.TimeSeriesExpanded>
          enableNone
          value={template?.id}
          setValue={(id) => setTemplate(timeSeries.find((u) => u.id == id))}
          type={ts.enums.RESOURCES_TYPES_ENUM.TIME_SERIES}
          inputProps={{
            fullWidth: true,
            label: 'Template',
            variant: 'outlined',
            required: true,
            size: 'small',
          }}
          filter={(ts) => !ts.source_resource_id}
        />
      </mui.core.Box>
      <ui.WorkspaceSelector value={workspace} setValue={setWorkspace} />
    </mui.core.Box>
  );

  return (
    <mui.core.Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      {errorMessage && (
        <mui.core.Alert onClose={() => setErrorMessage('')} severity="error">
          {errorMessage}
        </mui.core.Alert>
      )}
      <ui.DialogTitle closeAction={() => setOpen(false)}>Create Time Series</ui.DialogTitle>

      <mui.core.DialogContent>{formContent()}</mui.core.DialogContent>

      <mui.core.DialogActions>
        <ui.Button
          variant="contained"
          color="primary"
          loading={loadingCreate}
          onClick={handleSubmit}
          style={{ margin: '0rem 1rem 1rem' }}
          fullWidth
        >
          {'Create Time Series'}
        </ui.Button>
      </mui.core.DialogActions>
    </mui.core.Dialog>
  );
};

export default TimeSeriesForm;
